import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Card, EmptyState } from '@liftango/ui-elements';
import { opsColors, opsElevation, opsFontFamily, opsFontSize, opsFontWeight, opsRadius, opsSpace, opsZIndex } from '../../../../../theme/opsTokens';
import type { RunReport } from '../useRunReport';
import { formatClock, formatNumber } from './reportFormat';
import { ganttRows, hourTicks, type GanttRow, type GanttSegment, type OccupancyLevel } from './chartData';
import { vehicleName } from './railLabels';
import { SourceProblem } from './SourceProblem';

const LABEL_COLUMN = '168px';
const HEADER_HEIGHT = '28px';
const ROW_HEIGHT = '32px';
const SEGMENT_INSET = '9px';
const HAIRLINE = '1px solid';
const FOCUS_RING = '2px solid';
const FOCUS_OFFSET = '-2px';
const TICK_HOURS = 2;
const TOOLTIP_OFFSET_PX = 12;
const OCCUPANCY_LEVELS: readonly OccupancyLevel[] = [0.25, 0.5, 0.75, 1];
const SWATCH = { width: '12px', height: '8px' } as const;
const BREAK_HATCH = `repeating-linear-gradient(45deg, ${opsColors.neutral.inkFaint} 0 2px, transparent 2px 4px)`;
const CENTRED = { top: '50%', transform: 'translate(-50%, -50%)' } as const;
const NOW_LINE_WIDTH = '2px';
const VISIBLE_ROWS = 10;
/** Each row's top hairline adds to its height. */
const ROW_BORDER = '1px';
const STICKY_HEADER = { position: 'sticky', top: 0, zIndex: 1 } as const;

export function occupancyColour(level: OccupancyLevel): string {
  return level === 0 ? opsColors.activity.deadhead.fill : alpha(opsColors.semantic.info.color, level);
}

interface Hover {
  readonly row: GanttRow;
  readonly segment: GanttSegment;
  readonly x: number;
  readonly y: number;
}

export interface VehicleGanttProps {
  readonly report: RunReport;
  readonly selectedShiftId: string | null;
  readonly onSelectShift: (shiftId: string | null) => void;
  /** The report clock; draws the "now" line across every row. */
  readonly nowMs: number | null;
}

const metaText = { fontFamily: opsFontFamily.mono, fontSize: opsFontSize.meta, color: opsColors.neutral.inkMute } as const;

export function VehicleGantt({ report, selectedShiftId, onSelectShift, nowMs }: VehicleGanttProps) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const [hover, setHover] = useState<Hover | null>(null);
  const day = report.derived?.day ?? null;
  const rows = useMemo(() => (report.derived && day ? ganttRows(report.derived.shifts, day) : []), [report.derived, day]);
  const ticks = useMemo(() => (day ? hourTicks(day, TICK_HOURS) : []), [day]);
  const toggle = useCallback(
    (shiftId: string) => onSelectShift(shiftId === selectedShiftId ? null : shiftId),
    [onSelectShift, selectedShiftId]
  );

  return (
    <Card data-testid="sim-results.report.gantt">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: opsSpace[4], flexWrap: 'wrap', mb: opsSpace[4] }}>
        <Box>
          <Box component="h2" sx={{ m: 0, fontSize: opsFontSize.h2, fontWeight: opsFontWeight.semibold, color: opsColors.neutral.ink }}>
            {t('results.report.gantt.title')}
          </Box>
          <Box sx={{ color: opsColors.neutral.inkSoft, fontSize: opsFontSize.sm }}>{t('results.report.gantt.subtitle')}</Box>
        </Box>
        <GanttLegend />
      </Box>
      {!report.derived ? (
        <SourceProblem report={report} sources={['activities', 'vehicles']} />
      ) : !day || rows.length === 0 ? (
        <EmptyState title={t('results.report.gantt.empty')} />
      ) : (
        // Beyond ten vehicles the rows scroll under a pinned time header.
        <Box data-testid="sim-results.report.gantt.scroll" sx={{ maxHeight: `calc(${HEADER_HEIGHT} + ${VISIBLE_ROWS} * (${ROW_HEIGHT} + ${ROW_BORDER}))`, overflowY: 'auto' }}>
          <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: `${LABEL_COLUMN} 1fr` }}>
            <Box aria-hidden="true" sx={{ ...STICKY_HEADER, height: HEADER_HEIGHT, display: 'flex', alignItems: 'center', px: opsSpace[2], backgroundColor: opsColors.neutral.panelAlt, ...metaText }}>
              {t('results.report.gantt.vehicleColumn')}
            </Box>
            <Box aria-hidden="true" sx={{ ...STICKY_HEADER, height: HEADER_HEIGHT, backgroundColor: opsColors.neutral.panelAlt }}>
              {ticks.map((tick) => (
                <Box key={tick.ms} component="span" sx={{ position: 'absolute', left: `${tick.pct}%`, ...CENTRED, ...metaText }}>
                  {formatClock(tick.ms, locale)}
                </Box>
              ))}
            </Box>
            {rows.map((row) => (
              <GanttLine key={row.shift.shiftId} row={row} selected={row.shift.shiftId === selectedShiftId} onToggle={toggle} onHover={setHover} />
            ))}
            {nowMs != null && nowMs >= day.startMs && nowMs <= day.endMs ? (
              <Box
                aria-hidden="true"
                data-testid="sim-results.report.gantt.now"
                // Per-frame position goes in style: a changing sx value adds a CSS rule every frame.
                style={{ left: `calc(${LABEL_COLUMN} + (100% - ${LABEL_COLUMN}) * ${(nowMs - day.startMs) / (day.endMs - day.startMs)})` }}
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: NOW_LINE_WIDTH,
                  pointerEvents: 'none',
                  backgroundColor: opsColors.brand.primary,
                }}
              />
            ) : null}
          </Box>
        </Box>
      )}
      {hover ? <SegmentTooltip hover={hover} /> : null}
    </Card>
  );
}

function occupancySummary(row: GanttRow, t: TFunction, locale: string): string {
  return row.segments
    .filter((segment) => segment.level > 0)
    .map((segment) =>
      t('results.report.gantt.stretch', {
        start: formatClock(segment.startMs, locale),
        end: formatClock(segment.endMs, locale),
        aboard: formatNumber(segment.aboard, locale),
        seats: formatNumber(segment.seats, locale),
      })
    )
    .join('; ');
}

interface GanttLineProps {
  readonly row: GanttRow;
  readonly selected: boolean;
  readonly onToggle: (shiftId: string) => void;
  readonly onHover: (hover: Hover | null) => void;
}

const GanttLine = memo(function GanttLine({ row, selected, onToggle, onHover }: GanttLineProps) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const name = vehicleName(row.shift, t);
  const background = selected ? opsColors.brand.primarySoft : 'transparent';
  const descriptionId = `sim-results-gantt-${row.shift.shiftId}`;
  const select = () => onToggle(row.shift.shiftId);
  const showAt = (segment: GanttSegment) => (event: React.MouseEvent) => onHover({ row, segment, x: event.clientX, y: event.clientY });
  const hide = () => onHover(null);

  return (
    <Box sx={{ display: 'contents' }} data-testid={`sim-results.report.gantt.vehicle.${row.shift.ordinal}`} data-selected={selected}>
      <Box
        component="button"
        type="button"
        aria-pressed={selected}
        aria-label={t('results.report.gantt.rowLabel', {
          name,
          start: formatClock(row.shift.startMs, locale),
          end: formatClock(row.shift.endMs, locale),
          count: row.shift.trips,
        })}
        aria-describedby={descriptionId}
        onClick={select}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          height: ROW_HEIGHT,
          px: opsSpace[2],
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: background,
          borderTop: `${HAIRLINE} ${opsColors.neutral.line}`,
          '&:focus-visible': { outline: `${FOCUS_RING} ${opsColors.brand.primary}`, outlineOffset: FOCUS_OFFSET },
        }}
      >
        <Box component="span" sx={{ fontFamily: opsFontFamily.mono, fontSize: opsFontSize.xs, fontWeight: opsFontWeight.semibold, color: opsColors.neutral.ink }}>
          {name}
        </Box>
        <Box component="span" sx={metaText}>
          {formatClock(row.shift.startMs, locale)}–{formatClock(row.shift.endMs, locale)}
        </Box>
        <Box component="span" id={descriptionId} sx={{ display: 'none' }}>
          {occupancySummary(row, t, locale)}
        </Box>
      </Box>
      <Box
        aria-hidden="true"
        data-testid="sim-results.report.gantt.track"
        onClick={select}
        onMouseLeave={hide}
        sx={{ position: 'relative', overflow: 'hidden', height: ROW_HEIGHT, cursor: 'pointer', backgroundColor: background, borderTop: `${HAIRLINE} ${opsColors.neutral.line}` }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${row.shiftLeftPct}%`,
            width: `${row.shiftWidthPct}%`,
            top: SEGMENT_INSET,
            bottom: SEGMENT_INSET,
            border: `${HAIRLINE} ${opsColors.neutral.lineStrong}`,
            borderRadius: opsRadius.xs,
          }}
        />
        {row.segments.map((segment) => (
          <Box
            key={segment.startMs}
            data-testid="sim-results.report.gantt.segment"
            data-level={segment.level}
            onMouseEnter={showAt(segment)}
            onMouseMove={showAt(segment)}
            onMouseLeave={hide}
            sx={{
              position: 'absolute',
              left: `${segment.leftPct}%`,
              width: `${segment.widthPct}%`,
              top: SEGMENT_INSET,
              bottom: SEGMENT_INSET,
              backgroundColor: occupancyColour(segment.level),
            }}
          />
        ))}
        {row.pickPcts.map((at, index) => (
          <Tick key={`p${index}`} at={at} glyph="▼" colour={opsColors.neutral.ink} edge="top" kind="pick" />
        ))}
        {row.dropPcts.map((at, index) => (
          <Tick key={`d${index}`} at={at} glyph="▲" colour={opsColors.neutral.inkMute} edge="bottom" kind="drop" />
        ))}
      </Box>
    </Box>
  );
});

function Tick({ at, glyph, colour, edge, kind }: { at: number; glyph: string; colour: string; edge: 'top' | 'bottom'; kind: 'pick' | 'drop' }) {
  return (
    <Box
      component="span"
      data-testid={`sim-results.report.gantt.${kind}`}
      data-at={at}
      sx={{ position: 'absolute', left: `${at}%`, [edge]: 0, transform: 'translateX(-50%)', fontSize: opsFontSize.micro, lineHeight: 1, color: colour, pointerEvents: 'none' }}
    >
      {glyph}
    </Box>
  );
}

function SegmentTooltip({ hover }: { hover: Hover }) {
  const { t, i18n } = useTranslation('simulation');
  const locale = i18n.language;
  const { segment, row } = hover;
  return (
    <Box
      aria-hidden="true"
      data-testid="sim-results.report.gantt.tooltip"
      sx={{
        position: 'fixed',
        left: hover.x + TOOLTIP_OFFSET_PX,
        top: hover.y + TOOLTIP_OFFSET_PX,
        zIndex: opsZIndex.tooltip,
        pointerEvents: 'none',
        backgroundColor: opsColors.neutral.ink,
        color: opsColors.neutral.inkOnAccent,
        borderRadius: opsRadius.md,
        boxShadow: opsElevation.floating.shadow,
        p: opsSpace[2],
        fontSize: opsFontSize.xs,
      }}
    >
      <Box sx={{ fontWeight: opsFontWeight.semibold }}>
        {vehicleName(row.shift, t)} · {t(segment.level === 0 ? 'results.report.gantt.emptySegment' : 'results.report.gantt.carrying')}
      </Box>
      <Box sx={{ fontFamily: opsFontFamily.mono }}>
        {formatClock(segment.startMs, locale)}–{formatClock(segment.endMs, locale)}
      </Box>
      <Box>{t('results.report.gantt.aboard', { aboard: formatNumber(segment.aboard, locale), seats: formatNumber(segment.seats, locale) })}</Box>
    </Box>
  );
}

function LegendItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: opsSpace[1] }}>
      <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', gap: opsSpace[0.5] }}>
        {children}
      </Box>
      {label}
    </Box>
  );
}

function Swatch({ background, border }: { background: string; border?: string }) {
  return <Box component="span" sx={{ ...SWATCH, display: 'inline-block', background, border }} />;
}

function GanttLegend() {
  const { t } = useTranslation('simulation');
  const label = (key: string) => t(`results.report.gantt.legend.${key}`);
  return (
    <Box data-testid="sim-results.report.gantt.legend" sx={{ display: 'flex', flexWrap: 'wrap', gap: opsSpace[3], fontSize: opsFontSize.meta, color: opsColors.neutral.inkSoft }}>
      <LegendItem label={label('pickUp')}>▼</LegendItem>
      <LegendItem label={label('dropOff')}>▲</LegendItem>
      <LegendItem label={label('shift')}>
        <Swatch background="transparent" border={`${HAIRLINE} ${opsColors.neutral.lineStrong}`} />
      </LegendItem>
      <LegendItem label={label('empty')}>
        <Swatch background={occupancyColour(0)} />
      </LegendItem>
      <LegendItem label={label('break')}>
        <Swatch background={BREAK_HATCH} />
      </LegendItem>
      <LegendItem label={label('seatsFilled')}>
        {OCCUPANCY_LEVELS.map((level) => (
          <Swatch key={level} background={occupancyColour(level)} />
        ))}
      </LegendItem>
    </Box>
  );
}
